import React, { useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  Body,
  CloseAction,
  CloseButton,
  Header,
  Overlay,
  Panel,
  Title,
} from './styles';

export interface PendencyModalProps {
  open: boolean;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  closeLabel?: string;
  width?: number;
  closeOnOverlayClick?: boolean;
  className?: string | undefined;
}

const DismissIcon = (): JSX.Element => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const PendencyModal = ({
  open,
  title,
  children,
  onClose,
  closeLabel = 'Fechar',
  width = 352,
  closeOnOverlayClick = true,
  className,
}: PendencyModalProps): JSX.Element | null => {
  const pressedOnOverlay = useRef(false);

  const handleOverlayMouseDown = (
    event: React.MouseEvent<HTMLDivElement>
  ): void => {
    pressedOnOverlay.current = event.target === event.currentTarget;
  };

  const handleOverlayClick = (
    event: React.MouseEvent<HTMLDivElement>
  ): void => {
    const startedAndEndedOnOverlay =
      pressedOnOverlay.current && event.target === event.currentTarget;
    pressedOnOverlay.current = false;

    if (closeOnOverlayClick && startedAndEndedOnOverlay) onClose();
  };

  if (!open) return null;

  const content = (
    <Overlay
      role="presentation"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <Panel
        $width={width}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={className}
        onClick={(event) => event.stopPropagation()}
      >
        <Header>
          <Title>{title}</Title>
          <CloseButton
            type="button"
            aria-label="Fechar janela"
            onClick={onClose}
          >
            <DismissIcon />
          </CloseButton>
        </Header>

        <Body>{children}</Body>

        <CloseAction type="button" onClick={onClose}>
          {closeLabel}
        </CloseAction>
      </Panel>
    </Overlay>
  );

  return createPortal(content, document.body);
};

export default PendencyModal;
